import { useRef, useState } from "react";
import { UploadCloud, X, Star, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

export default function ImageUploader({ value = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const inputRef = useRef(null);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    setUploading(true);
    const added = [];
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(toAbsolute(data.url));
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Failed to upload ${file.name}`);
      }
    }
    if (added.length) {
      onChange([...value, ...added]);
      toast.success(`${added.length} photo${added.length > 1 ? "s" : ""} uploaded`);
    }
    setUploading(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const makeCover = (idx) => {
    const next = [...value];
    const [item] = next.splice(idx, 1);
    onChange([item, ...next]);
  };
  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    onChange([...value, u]);
    setUrlInput("");
  };

  return (
    <div className="space-y-4">
      {value.length > 0 && (
        <div data-testid="uploader-thumbs" className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {value.map((src, i) => (
            <div key={i} className="group relative aspect-square bg-muted border border-border overflow-hidden">
              <img src={src} alt="" className="h-full w-full object-cover" />
              {i === 0 && (
                <span className="absolute top-1 left-1 overline bg-foreground text-background px-1.5 py-0.5 text-[9px]">Cover</span>
              )}
              <div className="absolute inset-0 bg-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {i !== 0 && (
                  <button type="button" data-testid={`make-cover-${i}`} onClick={() => makeCover(i)} title="Set as cover"
                    className="text-background hover:scale-110 transition-transform"><Star size={18} /></button>
                )}
                <button type="button" data-testid={`remove-image-${i}`} onClick={() => remove(i)} title="Remove"
                  className="text-background hover:scale-110 transition-transform"><X size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        data-testid="upload-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`cursor-pointer border border-dashed p-8 flex flex-col items-center justify-center gap-2 text-center transition-colors ${dragOver ? "border-foreground bg-secondary/50" : "border-border"}`}
      >
        {uploading ? <Loader2 className="animate-spin" size={22} /> : <UploadCloud size={22} className="text-muted-foreground" />}
        <span className="text-sm">{uploading ? "Uploading…" : "Drag & drop photos, or click to browse"}</span>
        <span className="overline text-muted-foreground">JPG · PNG · WEBP · up to 10 MB</span>
        <input ref={inputRef} data-testid="upload-input" type="file" accept="image/*" multiple hidden
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
      </div>

      <div className="flex gap-2">
        <div className="flex-1 flex items-center border border-border px-3">
          <Link2 size={15} className="text-muted-foreground shrink-0" />
          <input
            data-testid="url-input"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
            placeholder="…or paste an image URL"
            className="w-full bg-transparent py-2.5 px-2 text-sm focus:outline-none font-mono text-xs"
          />
        </div>
        <button type="button" data-testid="add-url-btn" onClick={addUrl}
          className="border border-foreground px-4 overline hover-invert hover:bg-foreground hover:text-background">Add</button>
      </div>
    </div>
  );
}
